
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { DEFAULT_SEO_DESCRIPTION, DEFAULT_SEO_TITLE } from '@/lib/seo';
import { FlightBooking } from '@/api/entities';
import { User } from '@/api/entities';
import { UserPreferences } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Link, useLocation, useParams } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Plus, Plane, TrendingUp, X, CheckCircle, Info, AlertCircle, Search, WifiOff } from 'lucide-react';
import { isNativeApp } from '@/native/bootstrap';
import { isOffline, offlineAccount, saveOfflineTrips, loadOfflineTrips } from '@/native/offline';
import { useNavigate } from 'react-router-dom';
import { getCityName } from '@/components/airportUtils';
import { cn } from '@/lib/utils';
import RoyalAmbientBackground from '@/components/ui/RoyalAmbientBackground';
import {
  CART_SECTION_CARD,
  DASHBOARD_FILTER_INPUT,
  DASHBOARD_TAB_ACTIVE,
  DASHBOARD_TAB_INACTIVE,
} from '@/constants/brandColors';

import FlightListItem from '../components/flight/FlightListItem';
import { useTripsFlightStatus } from '@/hooks/useFlightLive';
import { cardSegment } from '@/utils/flightStatusDisplay';
import FlightDetailsModal from '../components/flight/FlightDetailsModal';
import BookingSuccessCelebration from '../components/booking/BookingSuccessCelebration';
import ServiceInquiryCard from '@/components/support/ServiceInquiryCard';
import NativeTripsPrompt from '@/components/native/NativeTripsPrompt';
import { portfolioTravelValue } from '@/utils/travelTotals';

export default function Dashboard() {
  const [allFlights, setAllFlights] = useState([]);
  const [trips, setTrips] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showFlightModal, setShowFlightModal] = useState(false);
  const [user, setUser] = useState(null);
  const [userMap, setUserMap] = useState(new Map());
  const [searchTerm, setSearchTerm] = useState('');
  const [extensionRequests, setExtensionRequests] = useState([]);
  const [dismissedRequests, setDismissedRequests] = useState(new Set());
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState('all'); // This state will remain, but the UI component to change it will be removed.
  const [filters, setFilters] = useState({
    airline: 'all',
    departureCity: 'all',
    arrivalCity: 'all',
    flightsCount: 'all',
    status: 'all',
  });
  const [showBookingCelebration, setShowBookingCelebration] = useState(false);
  const [deferaZeroPaymentSuccessBookingId, setDeferaZeroPaymentSuccessBookingId] = useState(null);
  // When set, the trips on screen are the copy saved on the phone at this
  // time (iOS app, no connection) — see src/native/offline.js.
  const [savedTripsAt, setSavedTripsAt] = useState(null);
  const location = useLocation();
  const params = useParams();

  // Fetch pending extension requests for admins
  const fetchExtensionRequests = useCallback(async () => {
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) return;

      const response = await fetch('/api/admin/extension-requests', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setExtensionRequests(data.requests || []);
        // Don't clear dismissed requests - they should remain permanently dismissed
        // The filtering happens in the render to hide dismissed requests
      }
    } catch (error) {
      console.error('Error fetching extension requests:', error);
    }
  }, []);

  const loadDashboardData = useCallback(async (flightIdToShowFromUrl, { refreshOpenTrip = false } = {}) => {
    console.log('🚀 [Dashboard] loadDashboardData called');
    setIsLoading(true);

    // Group bookings into trips (newest first) and open the trip a link
    // pointed at. Used for fresh loads and for the copy saved on the phone.
    const showFlights = (flightsData) => {
      setAllFlights(flightsData);

      const tripsMap = new Map();
      flightsData.forEach((flight) => {
        const key = flight.trip_reference || flight.booking_reference || flight.itinerary_image_url || flight.id;
        if (!tripsMap.has(key)) {
          tripsMap.set(key, []);
        }
        tripsMap.get(key).push(flight);
      });

      const getEarliestFlightInTrip = (trip) => {
        if (trip.length === 0) return null;
        return trip.reduce((earliest, current) => {
          const earliestDateTime = new Date(`${earliest.departure_date}T${earliest.departure_time || '00:00'}`);
          const currentDateTime = new Date(`${current.departure_date}T${current.departure_time || '00:00'}`);
          return currentDateTime < earliestDateTime ? current : earliest;
        });
      };

      const sortedTrips = Array.from(tripsMap.values()).sort((tripA, tripB) => {
        const firstFlightA = getEarliestFlightInTrip(tripA);
        const firstFlightB = getEarliestFlightInTrip(tripB);
        if (!firstFlightA && !firstFlightB) return 0;
        if (!firstFlightA) return 1;
        if (!firstFlightB) return -1;
        return new Date(firstFlightB.created_date).getTime() - new Date(firstFlightA.created_date).getTime();
      });

      setTrips(sortedTrips);

      const tripKey = (f) => f.trip_reference || f.booking_reference || f.itinerary_image_url || f.id;
      if (refreshOpenTrip) {
        // Back online with a trip open from the saved copy: show it live.
        setSelectedTrip((open) => {
          if (!open?.length) return open;
          const fresh = flightsData.filter((f) => tripKey(f) === tripKey(open[0]));
          return fresh.length > 0 ? fresh : open;
        });
      }

      if (flightIdToShowFromUrl) {
        // Try to find by booking_reference first, then fall back to ID (using String comparison for type safety)
        const flightToShow = flightsData.find((f) =>
          f.booking_reference === flightIdToShowFromUrl ||
          f.trip_reference === flightIdToShowFromUrl ||
          String(f.id) === String(flightIdToShowFromUrl)
        );

        if (flightToShow) {
          const groupKey = tripKey(flightToShow);
          const tripFlights = flightsData.filter((f) => tripKey(f) === groupKey);
          setSelectedTrip(tripFlights.length > 0 ? tripFlights : [flightToShow]);
          setShowFlightModal(true);
        }
      }
    };

    // iOS app that can't reach Defera: the trips saved on the phone.
    const showSavedTrips = () => {
      if (!isNativeApp) return false;
      const account = offlineAccount();
      const saved = account ? loadOfflineTrips(account.id) : null;
      if (!saved) return false;
      setUser(account);
      showFlights(saved.bookings);
      setSavedTripsAt(saved.savedAt);
      setNotification(null);
      return true;
    };

    if (isOffline() && showSavedTrips()) {
      setIsLoading(false);
      return;
    }

    try {
      console.log('🔍 [Dashboard] Fetching current user...');
      const currentUser = await User.me();
      console.log('✅ [Dashboard] Current user:', currentUser?.email, currentUser?.role);
      setUser(currentUser);

      if (!currentUser) {
        // Signed in on this phone but Supabase couldn't be reached to confirm it.
        if (showSavedTrips()) return;
        console.warn('⚠️ [Dashboard] No current user found, stopping data load');
        setIsLoading(false);
        return;
      }

      // For admin users, load user map to show names instead of booking references
      // SECURITY: Check app_metadata.roles (secure) not just user.role
      const roles = currentUser.app_metadata?.roles || [];
      const isActuallyAdmin = roles.includes('admin');
      const isInvestor = roles.includes('investor') || currentUser.role === 'investor';
      const isBacker = roles.includes('backer');
      
      console.log('🔍 [Dashboard] User role check:', { 
        userRole: currentUser.role, 
        appMetadataRoles: roles,
        isActuallyAdmin,
        isInvestor,
        isBacker
      });
      
      // Load user map asynchronously (non-blocking) for admin
      if (isActuallyAdmin) {
        // Don't await - load user map in background so it doesn't block data loading
        User.list().then(users => {
          console.log('✅ [Dashboard] User.list() returned', users?.length || 0, 'users');
          const newUserMap = new Map();
          users.forEach((u) => {
            // Create display name from first and last name, or fall back to email
            let displayName;
            if (u.firstName && u.lastName) {
              displayName = `${u.firstName} ${u.lastName}`;
            } else if (u.firstName) {
              displayName = u.firstName;
            } else if (u.lastName) {
              displayName = u.lastName;
            } else if (u.email) {
              // Fallback: parse email if no name available
              displayName = u.email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            } else {
              displayName = 'Unknown User';
            }
            newUserMap.set(u.id, displayName);
          });
          setUserMap(newUserMap);
          console.log('✅ [Dashboard] User map loaded successfully');
        }).catch(error => {
          console.error('❌ [Dashboard] Failed to load user map for admin:', error);
          // Continue without user map - this is non-fatal
        });
      }
      
      // SECURITY: pick the right data source by role.
      //   - Admins see all bookings → FlightBooking.list() (routes to /api/admin/all-bookings)
      //   - Investors + backers see ONLY their own customer bookings →
      //       FlightBooking.listMyOwn() (routes to /api/user/bookings).
      //     We do NOT call list() for them, because that would put backer-assigned
      //     and/or all-bookings data into the network response — even though the UI
      //     would filter it away client-side, it would still be visible in DevTools.
      //   - Regular users see only their own bookings → FlightBooking.list() (routes
      //       to /api/user/bookings already).
      let flightsData;
      if ((isInvestor || isBacker) && !isActuallyAdmin) {
        console.log('🔍 [Dashboard] Investor/Backer path → FlightBooking.listMyOwn()');
        flightsData = await FlightBooking.listMyOwn();
      } else {
        console.log('🔍 [Dashboard] Default path → FlightBooking.list()');
        flightsData = await FlightBooking.list('-created_at');
      }
      console.log('✅ [Dashboard] Bookings fetched:', flightsData?.length || 0);

      // Ensure flightsData is an array
      if (!Array.isArray(flightsData)) {
        console.warn('⚠️ [Dashboard] flightsData is not an array:', flightsData);
        flightsData = [];
      }

      console.log('📊 [Dashboard] Setting flights data:', flightsData.length, 'flights');
      showFlights(flightsData);
      // Keep a copy on the phone for when there's no signal (iOS app only;
      // never for admins — see src/native/offline.js).
      saveOfflineTrips(currentUser, flightsData);
      setSavedTripsAt(null);

      // If admin, fetch extension requests and load dismissed IDs
      if (isActuallyAdmin) {
        fetchExtensionRequests();
        // Load dismissed extension request IDs from user preferences
        try {
          const dismissedIds = await UserPreferences.getDismissedExtensionRequests();
          setDismissedRequests(new Set(dismissedIds));
        } catch (error) {
          console.error('Error loading dismissed extension requests:', error);
        }
      }

    } catch (error) {
      // Couldn't reach Defera: the trips saved on the phone beat an error.
      if (showSavedTrips()) return;
      console.error('Error loading dashboard data:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      // Ensure we set loading to false even on error
      setAllFlights([]);
      setTrips([]);
      setIsLoading(false);
      
      // Show meaningful error to user if it's an auth issue
      if (error.message && (error.message.includes('session') || error.message.includes('authenticate') || error.message.includes('log in'))) {
        setNotification({ 
          type: 'error', 
          message: error.message 
        });
      } else {
        setNotification({ 
          type: 'error', 
          message: 'Unable to load your trips. Please refresh the page.' 
        });
      }
      
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const sessionBookingId = sessionStorage.getItem('open_booking_id');
    const flightIdToShow = params.id || queryParams.get('show_flight_id') || queryParams.get('tripId') || sessionBookingId;
    
    if (sessionBookingId) {
      sessionStorage.removeItem('open_booking_id');
    }

    loadDashboardData(flightIdToShow);

    if (queryParams.get('defera_zero') === 'success' || queryParams.get('defera_capital') === 'success') {
      setShowBookingCelebration(true);
      // Ezra telemetry: the bottom of the search→checkout funnel. Safe from
      // double-fires — the success param is stripped by replaceState below.
      import('@/lib/activityTracker').then(({ trackEvent }) => {
        trackEvent('checkout_completed', {
          provider: queryParams.get('defera_zero') === 'success' ? 'defera_zero' : 'defera_capital',
        });
      }).catch(() => {});
    } else if (queryParams.get('defera_zero') === 'payment_success' && flightIdToShow) {
      setDeferaZeroPaymentSuccessBookingId(flightIdToShow);
    } else if (queryParams.get('payment_initiated') === 'true') {
      setNotification({ type: 'info', message: 'Your payment plan is ready!' });
    } else if (queryParams.get('payment') === 'success') {
      setNotification({ type: 'success', message: 'Payment successful! Your flight plan is confirmed.' });
    }

    if (location.search) {
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.search, location.pathname, params.id]);

  // Showing the copy saved on the phone: swap in live trips as soon as the
  // connection is back (or the app is reopened with one).
  useEffect(() => {
    if (!savedTripsAt) return undefined;
    let timer;
    const reload = () => {
      if (isOffline()) return;
      clearTimeout(timer);
      // A moment for the connection to settle before the first request.
      timer = setTimeout(() => loadDashboardData(undefined, { refreshOpenTrip: true }), 1500);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') reload();
    };
    window.addEventListener('online', reload);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('online', reload);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [savedTripsAt, loadDashboardData]);

  // Poll for new extension requests every 30 seconds (admin only)
  useEffect(() => {
    if (user && (user.role === 'admin' || user.app_metadata?.roles?.includes('admin'))) {
      const interval = setInterval(() => {
        fetchExtensionRequests();
      }, 30000); // Poll every 30 seconds

      return () => clearInterval(interval);
    }
  }, [user, fetchExtensionRequests]);

  // Auto-resume interrupted Reserve shortfall checkouts.
  //
  // Scenario this handles: a customer starts a Reserve booking with a shortfall, the
  // card charge succeeds, but the tab dies (network drop, crash, closed early) BEFORE
  // the reserve debit and booking row land. The /api/reserve/booking-intent/pending
  // endpoint finds any of their intents stuck at 'charged' or 'debited' within the last
  // 7 days, and /resume finishes them atomically. Without this, she'd see extra money
  // in her Reserve balance but no trip — the "Nae Saelee" failure class.
  useEffect(() => {
    if (!user || isOffline()) return;
    let cancelled = false;
    (async () => {
      try {
        const { supabase } = await import('@/lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token || cancelled) return;
        const pendingRes = await fetch('/api/reserve/booking-intent/pending', {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
        // 404: endpoint not rolled out yet — silent no-op.
        // Non-2xx: something transient — try again next mount.
        if (!pendingRes.ok || cancelled) return;
        const { pending = [] } = await pendingRes.json().catch(() => ({}));
        if (!Array.isArray(pending) || pending.length === 0) return;

        for (const intent of pending) {
          if (cancelled) return;
          try {
            const resumeRes = await fetch(`/api/reserve/booking-intent/${intent.id}/resume`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${session.access_token}` }
            });
            const resumeJson = await resumeRes.json().catch(() => ({}));
            if (resumeJson?.success && resumeJson?.status === 'booked') {
              setNotification({
                type: 'success',
                message: 'We finished your previous checkout — your trip is confirmed below.'
              });
              // Reload to surface the newly-created booking row.
              loadDashboardData();
            }
          } catch (resumeErr) {
            console.warn('Booking intent resume failed', intent.id, resumeErr);
          }
        }
      } catch (err) {
        console.warn('Pending booking intent check failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user, loadDashboardData]);

  const handleFlightClick = (clickedFlight) => {
    // Use trip_reference for grouping, fall back to booking_reference, itinerary_image_url, then id
    const groupKey = clickedFlight.trip_reference || clickedFlight.booking_reference || clickedFlight.itinerary_image_url || clickedFlight.id;
    const tripFlights = allFlights.filter(
      (f) => {
        const fKey = f.trip_reference || f.booking_reference || f.itinerary_image_url || f.id;
        return fKey === groupKey;
      }
    );
    setSelectedTrip(tripFlights.length > 0 ? tripFlights : [clickedFlight]);
    setShowFlightModal(true);
  };

  const closeFlightModal = useCallback(() => {
    setShowFlightModal(false);
    setSelectedTrip(null);
  }, []);

  const handleDeferaZeroPaymentSuccessHandled = useCallback(() => {
    setDeferaZeroPaymentSuccessBookingId(null);
  }, []);

  const handleModalUpdate = useCallback(async () => {
    // Reload dashboard data after admin edits a trip
    try {
      const flightsData = await FlightBooking.list('-created_at');
      setAllFlights(flightsData);

      const tripsMap = new Map();
      flightsData.forEach((flight) => {
        const key = flight.trip_reference || flight.booking_reference || flight.itinerary_image_url || flight.id;
        if (!tripsMap.has(key)) {
          tripsMap.set(key, []);
        }
        tripsMap.get(key).push(flight);
      });

      const getEarliestFlightInTrip = (trip) => {
        if (trip.length === 0) return null;
        return trip.reduce((earliest, current) => {
          const earliestDateTime = new Date(`${earliest.departure_date}T${earliest.departure_time || '00:00'}`);
          const currentDateTime = new Date(`${current.departure_date}T${current.departure_time || '00:00'}`);
          return currentDateTime < earliestDateTime ? current : earliest;
        });
      };

      const sortedTrips = Array.from(tripsMap.values()).sort((tripA, tripB) => {
        const firstFlightA = getEarliestFlightInTrip(tripA);
        const firstFlightB = getEarliestFlightInTrip(tripB);
        if (!firstFlightA && !firstFlightB) return 0;
        if (!firstFlightA) return 1;
        if (!firstFlightB) return -1;
        return new Date(firstFlightB.created_date).getTime() - new Date(firstFlightA.created_date).getTime();
      });

      setTrips(sortedTrips);

      // If there's a selected trip, update it with fresh data
      if (selectedTrip && selectedTrip[0]) {
        const groupKey = selectedTrip[0].trip_reference || selectedTrip[0].booking_reference || selectedTrip[0].itinerary_image_url || selectedTrip[0].id;
        const updatedTrip = flightsData.filter((f) => {
          const fKey = f.trip_reference || f.booking_reference || f.itinerary_image_url || f.id;
          return fKey === groupKey;
        });
        if (updatedTrip.length > 0) {
          setSelectedTrip(updatedTrip);
        }
      }
    } catch (error) {
      console.error('Failed to reload dashboard data:', error);
    }
  }, [selectedTrip]);

  // Booked value of the portfolio: travel only, a Faye premium is collected for Faye.
  const totalValue = useMemo(() => portfolioTravelValue(allFlights), [allFlights]);

  // Live counts per trip status, used to power the segmented status tabs.
  // Each trip's status is determined by its primary flight (or first flight as
  // fallback) — same logic that the filter predicate uses below, kept in one
  // place to stay consistent.
  const statusCounts = useMemo(() => {
    const counts = { pending: 0, confirmed: 0, completed: 0, cancelled: 0, defaulted: 0 };
    trips.forEach(trip => {
      const primaryFlight = trip.find(f => f.is_primary_flight) || trip[0];
      const status = primaryFlight?.trip_status || primaryFlight?.status || 'pending';
      if (counts[status] !== undefined) counts[status] += 1;
    });
    return counts;
  }, [trips]);

  const notificationConfig = {
    success: { icon: CheckCircle, color: 'text-green-400' },
    info: { icon: Info, color: 'text-primary' },
    error: { icon: AlertCircle, color: 'text-red-400' }
  };

  const NotifIcon = notification ? notificationConfig[notification.type].icon : null;
  const notifColor = notification ? notificationConfig[notification.type].color : null;

  const isRegularUser = user && user.role !== 'admin';

  const showDeferaProductBadge =
    Boolean(user?.app_metadata?.roles?.includes('admin')) || user?.role === 'admin';

  const uniqueAirlines = useMemo(() => 
    [...new Set(allFlights.map(f => f.marketing_airline).filter(Boolean))].sort(),
    [allFlights]
  );
  
  const uniqueDepartureCities = useMemo(() => 
    [...new Set(allFlights.map(f => getCityName(f.departure_city)).filter(Boolean))].sort(),
    [allFlights]
  );
  
  const uniqueArrivalCities = useMemo(() => 
    [...new Set(allFlights.map(f => getCityName(f.arrival_city)).filter(Boolean))].sort(),
    [allFlights]
  );

  const filteredTrips = useMemo(() => trips.filter(trip => {
    // The selectedUser filter will implicitly be disabled if selectedUser is always 'all'
    // after removing the UI to change it.
    if (user?.role === 'admin' && selectedUser !== 'all' && trip[0].created_by !== selectedUser) {
      return false;
    }

    let searchMatch = true;
    if (searchTerm.trim() !== '') {
      const lowercasedSearchTerm = searchTerm.toLowerCase().trim();
      searchMatch = trip.some(flight => {
        // Basic flight info
        const creatorName = user?.role === 'admin' ? (userMap.get(flight.created_by) || '') : '';
        const departureCity = getCityName(flight.departure_city || '').toLowerCase();
        const arrivalCity = getCityName(flight.arrival_city || '').toLowerCase();
        const airline = (flight.marketing_airline || '').toLowerCase();
        const creator = creatorName.toLowerCase();
        
        // Booking identifiers
        const bookingRef = (flight.booking_reference || flight.bookingReference || '').toLowerCase();
        const tripRef = (flight.trip_reference || flight.tripReference || '').toLowerCase();
        const confirmationNumber = (flight.confirmation_number || flight.confirmationNumber || flight.airline_confirmation || '').toLowerCase();
        
        // User email (from userMap for admins, or from flight data)
        let userEmail = '';
        if (user?.role === 'admin' && flight.created_by) {
          // Try to get email from user map or flight data
          userEmail = (flight.user_email || '').toLowerCase();
        }
        
        // Passenger information from flight data
        const flightData = flight.flight_data || flight.flightData || {};
        const passengers = flightData.passengers || flight.passengers || [];
        const passengerNames = passengers.map(p => 
          `${p.first_name || p.firstName || p.given_name || p.givenName || ''} ${p.last_name || p.lastName || p.family_name || p.familyName || ''}`.toLowerCase().trim()
        ).filter(Boolean);
        const passengerEmails = passengers.map(p => (p.email || '').toLowerCase()).filter(Boolean);
        const passengerPhones = passengers.map(p => 
          (p.phone_number || p.phoneNumber || p.phone || '').replace(/[\s\-\(\)]/g, '').toLowerCase()
        ).filter(Boolean);
        
        // Search across all fields
        return (
          // Basic flight info
          departureCity.includes(lowercasedSearchTerm) ||
          arrivalCity.includes(lowercasedSearchTerm) ||
          airline.includes(lowercasedSearchTerm) ||
          (user?.role === 'admin' && creator.includes(lowercasedSearchTerm)) ||
          // Booking identifiers
          bookingRef.includes(lowercasedSearchTerm) ||
          tripRef.includes(lowercasedSearchTerm) ||
          confirmationNumber.includes(lowercasedSearchTerm) ||
          // User email
          (userEmail && userEmail.includes(lowercasedSearchTerm)) ||
          // Passenger names
          passengerNames.some(name => name.includes(lowercasedSearchTerm)) ||
          // Passenger emails
          passengerEmails.some(email => email.includes(lowercasedSearchTerm)) ||
          // Passenger phones (normalize search term to remove formatting)
          passengerPhones.some(phone => phone.includes(lowercasedSearchTerm.replace(/[\s\-\(\)]/g, '')))
        );
      });
    }

    const firstFlight = trip[0];
    const lastFlight = trip[trip.length - 1];

    const airlineMatch = filters.airline === 'all' || trip.some(f => f.marketing_airline === filters.airline);
    const departureMatch = filters.departureCity === 'all' || getCityName(firstFlight.departure_city) === filters.departureCity;
    const arrivalMatch = filters.arrivalCity === 'all' || getCityName(lastFlight.arrival_city) === filters.arrivalCity;
    
    let flightsCountMatch = true;
    if (filters.flightsCount !== 'all') {
      const count = trip.length;
      if (filters.flightsCount === '1') flightsCountMatch = count === 1;
      else if (filters.flightsCount === '2') flightsCountMatch = count === 2;
      else if (filters.flightsCount === '3+') flightsCountMatch = count >= 3;
    }

    // Status filter: check trip_status on primary flight (or first flight as fallback)
    const primaryFlight = trip.find(f => f.is_primary_flight) || firstFlight;
    const tripStatus = primaryFlight?.trip_status || primaryFlight?.status || 'pending';
    const statusMatch = filters.status === 'all' || tripStatus === filters.status;

    return searchMatch && airlineMatch && departureMatch && arrivalMatch && flightsCountMatch && statusMatch;
  }), [trips, user, selectedUser, searchTerm, filters, userMap]);

  // Live flight status for trips flying within a couple of days (one request
  // for the whole list; nothing for trips further out).
  const flightStatusByBooking = useTripsFlightStatus(filteredTrips);

  const handleFilterChange = useCallback((filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  }, []);

  return (
    <>
      <Helmet>
        <title>{DEFAULT_SEO_TITLE}</title>
        <meta name="description" content={DEFAULT_SEO_DESCRIPTION} />
      </Helmet>

      <AnimatePresence>
        {showBookingCelebration && (
          <BookingSuccessCelebration
            title="Your trip has been booked!"
            onClose={() => setShowBookingCelebration(false)}
          />
        )}
      </AnimatePresence>

      {/* Royal blue ambient wash — same treatment as the Rewards page */}
      <RoyalAmbientBackground />

      <div className="pb-12 relative z-10">
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={cn(CART_SECTION_CARD, 'p-4 mb-6 flex items-center justify-between')}
          >
            <div className="flex items-center gap-3">
              <NotifIcon className={`w-5 h-5 ${notifColor}`} />
              <p className="text-sm text-white/85 font-normal">{notification.message}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setNotification(null)} className="hover:bg-white/5 w-8 h-8 text-white/40 hover:text-white">
              <X className="w-4 h-4" />
            </Button>
          </motion.div>
        )}

        {savedTripsAt && (
          <div role="status" className={cn(CART_SECTION_CARD, 'p-4 mb-6 flex items-start gap-3')}>
            <WifiOff className="w-5 h-5 text-white/60 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-white/85 font-normal">
              {isOffline() ? "You're offline." : "Defera can't be reached right now."}{' '}
              These are your trips as of{' '}
              {new Date(savedTripsAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.
              <span className="text-white/60"> Payments and changes need a connection.</span>
            </p>
          </div>
        )}

        {/* Extension Request Banners (Admin Only) */}
        {user && (user.role === 'admin' || user.app_metadata?.roles?.includes('admin')) && (
          <div className="mb-6 space-y-3">
            {extensionRequests
              .filter(req => !dismissedRequests.has(req.id))
              .map((request) => (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={cn(CART_SECTION_CARD, 'p-4 flex items-center justify-between gap-4')}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <AlertCircle className="w-5 h-5 text-[var(--accent-color)] flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white font-medium mb-1">New Payment Extension Request</p>
                      <p className="text-xs text-white/85 font-normal">
                        {request.user_full_name || 'Unknown User'} - {request.booking_reference || 'N/A'}
                      </p>
                      <p className="text-xs text-white/60 font-normal">
                        {request.departure_city || 'N/A'} → {request.arrival_city || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Find the flight in allFlights and open the modal
                        const flight = allFlights.find(f => f.id === request.trip_id);
                        if (flight) {
                          handleFlightClick(flight);
                        } else {
                          // If not found, try to reload and then open
                          navigate(`/trips?tripId=${request.trip_id}`);
                        }
                      }}
                      className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-8 text-xs"
                    >
                      See Details
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        try {
                          // Save to database for permanent dismissal
                          await UserPreferences.dismissExtensionRequest(request.id);
                          // Update local state
                          setDismissedRequests(prev => new Set([...prev, request.id]));
                        } catch (error) {
                          console.error('Error dismissing extension request:', error);
                          // Still update local state even if database save fails
                          setDismissedRequests(prev => new Set([...prev, request.id]));
                        }
                      }}
                      className="text-white/60 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
          </div>
        )}

        {/* Header strip. The previous iteration kept four stacked zones
            (eyebrow + title + KPI cards + filter shell). The redesign
            collapses the masthead to a single horizontal line: title on
            the left, an inline metric ribbon on the right separated by
            hairline dividers — no card chrome at all. This reads like a
            real product-analytics page header instead of a marketing
            hero, and the eye no longer has to step over a column of
            heavy panels before reaching the actual list. */}
        <motion.div
          className="mb-5 mt-2 md:mt-4"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-6">
            <div className="min-w-0">
              <h1 className="text-3xl md:text-[32px] font-light text-white tracking-tight leading-none">
                {isRegularUser ? 'My Trips' : 'Trips'}
              </h1>
              <p className="text-white/40 text-[11px] font-normal mt-2.5 leading-none">
                {isRegularUser ? 'Track your upcoming and past journeys' : 'Manage all flight bookings and itineraries'}
              </p>
            </div>

            {/* Inline metric ribbon — no boxes, just text + thin dividers.
                Keeps the numbers visible without visually competing with
                the trip list below. Suppressed during the initial load
                so we don't flash a misleading "Total 0 / Portfolio $0"
                before the data resolves. */}
            {!isLoading && (
              <div className="flex items-center gap-4 md:gap-5 text-xs flex-wrap">
                <div className="flex items-center gap-2">
                  <Plane className="w-3 h-3 text-[hsl(228,35%,58%)]/80" />
                  <span className="text-white/35 uppercase tracking-[0.14em] text-[10px] font-normal">Total</span>
                  <span className="text-white/90 tabular-nums font-normal">{trips.length}</span>
                </div>
                {!isRegularUser && (
                  <>
                    <span className="w-px h-3 bg-white/10" aria-hidden="true" />
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-3 h-3 text-[hsl(228,35%,58%)]/80" />
                      <span className="text-white/35 uppercase tracking-[0.14em] text-[10px] font-normal">Portfolio</span>
                      <span className="text-white/90 tabular-nums font-normal">${Math.round(totalValue).toLocaleString()}</span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* iOS app only: a one-time offer of notifications, then of the
            Face ID lock. Renders nothing on the web. */}
        <NativeTripsPrompt />

        {/* Every open request the customer has with the team — including
            the ones not tied to a specific trip ("can I redeem miles for a
            gift card?"), which have no booking_reference and therefore
            render on no trip detail page at all.

            That gap mattered: Tommy tells queued customers their request is
            trackable, so there has to be one place where ALL of them are
            visible. This is the hub they land on, so this is that place.
            Trip-specific ones also appear on their own trip detail; the
            duplication is intentional — a drill-down should show what's
            outstanding on that trip. Renders nothing when nothing is open. */}
        {/* Not gated on role: the card queries the VIEWER's own inquiries
            (user_id-scoped server-side, plus RLS), so it's correct for
            anyone and renders nothing when they have none. An admin who
            filed a request is still the customer for that request. */}
        <div className="mb-5">
          <ServiceInquiryCard />
        </div>

        {/* Loading state — modern conic-gradient ring spinner that
            replaces the previous "dead space" between the page header
            and the trip list while the initial fetch is in flight.
            Two stacked masked layers form a hairline ring: a faint
            static track underneath, and a sweeping accent-colored
            gradient on top that smoothly fades from transparent to
            full intensity over ~280°, producing a comet-like trail
            instead of a hard-edged arc. The whole element gets a
            soft drop-shadow glow in the same royal-blue accent used
            by the status tabs and metric ribbon, so the moment feels
            on-brand rather than generic. */}
        {isLoading && (
          <motion.div
            className="flex flex-col items-center justify-center py-24 md:py-32"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            role="status"
            aria-live="polite"
            aria-label="Loading trips"
          >
            <div
              className="relative w-11 h-11"
              style={{ filter: 'drop-shadow(0 0 14px hsl(228 35% 58% / 0.35))' }}
            >
              {/* Hairline static track — gives the sweeping arc a
                  ghost rail to ride on so the ring still reads as a
                  full circle even when most of the gradient is faded
                  out. Mask carves the solid disk into a 1.5px ring. */}
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'hsl(228 35% 58% / 0.10)',
                  WebkitMask:
                    'radial-gradient(closest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1.5px))',
                  mask:
                    'radial-gradient(closest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1.5px))',
                }}
              />
              {/* Sweeping gradient arc — conic gradient runs 0→280°
                  from transparent to full accent, leaving an 80° gap
                  so the ring has a clear "head" and "tail" as it
                  rotates. animate-spin (1s linear) is sped up to
                  900ms for a slightly snappier, more modern cadence. */}
              <div
                className="absolute inset-0 rounded-full animate-spin"
                style={{
                  background:
                    'conic-gradient(from 0deg, transparent 0deg, hsl(228 35% 58% / 0) 20deg, hsl(228 35% 58%) 280deg, hsl(228 35% 58%) 320deg, transparent 360deg)',
                  WebkitMask:
                    'radial-gradient(closest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1.5px))',
                  mask:
                    'radial-gradient(closest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1.5px))',
                  animationDuration: '0.9s',
                }}
              />
              {/* Bright leading dot — sits at the head of the arc and
                  rotates with it. Adds a sense of motion and a tiny
                  point of light that catches the eye. */}
              <div
                className="absolute inset-0 animate-spin"
                style={{ animationDuration: '0.9s' }}
              >
                <span
                  className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[5px] h-[5px] rounded-full"
                  style={{
                    background: 'hsl(228 30% 72%)',
                    boxShadow:
                      '0 0 6px hsl(228 35% 58% / 0.9), 0 0 14px hsl(228 35% 58% / 0.6)',
                  }}
                />
              </div>
            </div>
            <p className="mt-6 text-[10px] uppercase tracking-[0.22em] text-white/35 font-normal">
              {isRegularUser ? 'Loading your trips' : 'Loading trips'}
            </p>
          </motion.div>
        )}

        {/* Status segmented tabs — replaces the old "Status" Select dropdown.
            Each tab carries its live count, so the page becomes a real
            switchboard: you click "Pending 12" to drill in, no menu
            interaction required. The active tab uses the same Royal-Blue
            accent already used by the metric ribbon and trip dashboard
            progress bar, so the page only ever speaks one accent color. */}
        {!isLoading && user?.role === 'admin' && (
          <motion.div
            className="mb-3 -mx-1 px-1 overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex gap-1.5 min-w-max">
              {[
                { value: 'all', label: 'All', count: trips.length },
                { value: 'pending', label: 'Pending', count: statusCounts.pending },
                { value: 'confirmed', label: 'Confirmed', count: statusCounts.confirmed },
                { value: 'completed', label: 'Completed', count: statusCounts.completed },
                { value: 'cancelled', label: 'Cancelled', count: statusCounts.cancelled },
                { value: 'defaulted', label: 'Defaulted', count: statusCounts.defaulted },
              ].map(tab => {
                const active = filters.status === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => handleFilterChange('status', tab.value)}
                    className={cn(
                      'h-8 px-3 rounded-full text-[11px] font-medium tracking-wide whitespace-nowrap transition-all flex items-center gap-1.5 border',
                      active ? DASHBOARD_TAB_ACTIVE : DASHBOARD_TAB_INACTIVE,
                    )}
                  >
                    <span>{tab.label}</span>
                    <span className={cn(
                      'tabular-nums font-normal text-[10px]',
                      active ? 'text-[hsl(228,35%,58%)]' : 'text-white/35'
                    )}>
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Slim toolbar: pill search + chip-style filter dropdowns inline.
            No glass shell wrapping the row — just the controls themselves.
            With the Status filter promoted to tabs above, this row is now
            calmer (4 chips instead of 5) and stops shouting for attention. */}
        {!isLoading && user?.role === 'admin' && (
          <motion.div
            className="mb-5 flex flex-col sm:flex-row gap-2"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(228,35%,58%)]/70 pointer-events-none" />
              <Input
                type="text"
                placeholder="Search city, airline, booking ref, email, phone, passenger…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={cn('pl-9 pr-4 h-9 rounded-full text-xs font-normal', DASHBOARD_FILTER_INPUT)}
              />
            </div>
            <div className="grid grid-cols-2 sm:flex sm:flex-nowrap gap-1.5">
              <Select value={filters.airline} onValueChange={(v) => handleFilterChange('airline', v)}>
                <SelectTrigger className={cn('h-9 rounded-full text-[11px] font-normal px-3.5 sm:w-[120px]', DASHBOARD_FILTER_INPUT, 'data-[state=open]:bg-brand-900/50 data-[state=open]:border-brand-700/40')}>
                  <SelectValue placeholder="Airline" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Airlines</SelectItem>
                  {uniqueAirlines.map(airline => (
                    <SelectItem key={airline} value={airline}>{airline}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.departureCity} onValueChange={(v) => handleFilterChange('departureCity', v)}>
                <SelectTrigger className={cn('h-9 rounded-full text-[11px] font-normal px-3.5 sm:w-[130px]', DASHBOARD_FILTER_INPUT, 'data-[state=open]:bg-brand-900/50 data-[state=open]:border-brand-700/40')}>
                  <SelectValue placeholder="From" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departures</SelectItem>
                  {uniqueDepartureCities.map(city => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.arrivalCity} onValueChange={(v) => handleFilterChange('arrivalCity', v)}>
                <SelectTrigger className={cn('h-9 rounded-full text-[11px] font-normal px-3.5 sm:w-[130px]', DASHBOARD_FILTER_INPUT, 'data-[state=open]:bg-brand-900/50 data-[state=open]:border-brand-700/40')}>
                  <SelectValue placeholder="To" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Arrivals</SelectItem>
                  {uniqueArrivalCities.map(city => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.flightsCount} onValueChange={(v) => handleFilterChange('flightsCount', v)}>
                <SelectTrigger className={cn('h-9 rounded-full text-[11px] font-normal px-3.5 sm:w-[110px]', DASHBOARD_FILTER_INPUT, 'data-[state=open]:bg-brand-900/50 data-[state=open]:border-brand-700/40')}>
                  <SelectValue placeholder="Flights" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any # of Flights</SelectItem>
                  <SelectItem value="1">1 Flight (Nonstop One-Way)</SelectItem>
                  <SelectItem value="2">2 Flights</SelectItem>
                  <SelectItem value="3+">3+ Flights</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </motion.div>
        )}

        {/* Disclaimer for regular users with 0 trips */}
        {!isLoading && trips && trips.length === 0 && (() => {
          // Check if user is a regular user (not admin, investor, or backer)
          // Only show when trips.length === 0 (already checked in outer condition)
          if (!user) return null;
          const roles = user.app_metadata?.roles || [];
          const isAdmin = roles.includes('admin') || user.role === 'admin';
          const isInvestor = roles.includes('investor') || user.role === 'investor';
          const isBacker = roles.includes('backer');
          
          // Only show for regular users (trips.length === 0 already checked in outer condition)
          if (isAdmin || isInvestor || isBacker) return null;
          
          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className={cn(CART_SECTION_CARD, 'rounded-full px-6 py-3 flex items-center justify-center gap-2')}
            >
              <Info className="w-3 h-3 text-white/60 flex-shrink-0" />
              <p className="text-xs text-white/85 font-normal">
                Please allow up to 2 hours for new bookings to populate in your Defera account.
              </p>
            </motion.div>
          );
        })()}

        {/* Trips List */}
        {filteredTrips.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {/* The "All Trips" eyebrow that used to live here was removed.
                With the segmented status tabs above, the active tab already
                tells you which slice of trips you're viewing (and shows the
                count next to it), so a separate list heading was duplicating
                information and adding chrome. */}
            <div className="space-y-2 sm:space-y-4">
              {filteredTrips.map((trip, index) => (
                <FlightListItem
                  key={trip[0].itinerary_image_url || `trip-${index}`}
                  trip={trip}
                  onClick={handleFlightClick}
                  creatorName={user?.role === 'admin' ? userMap.get(trip[0].userId || trip[0].created_by) : null}
                  creatorId={user?.role === 'admin' ? (trip[0].userId || trip[0].created_by || null) : null}
                  showPrice={user?.role === 'admin'}
                  showDeferaProductBadge={showDeferaProductBadge}
                  showInsuranceBadge={showDeferaProductBadge}
                  flightStatus={cardSegment(trip.flatMap((booking) => flightStatusByBooking[booking.id] || []))}
                />
              ))}
            </div>
          </motion.div>
        )}
        
        {/* Empty State */}
        {filteredTrips.length === 0 && !isLoading && (
          <motion.div
            className="relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className={cn(CART_SECTION_CARD, 'absolute inset-0 rounded-3xl')} />
            <div className="relative text-center py-20 px-6">
              <div className="w-20 h-20 mx-auto mb-6 rounded-3xl bg-brand-900/40 border border-brand-800/30 flex items-center justify-center">
                <Plane className="w-10 h-10 text-white/40" />
              </div>
              <h3 className="text-2xl font-light text-white mb-3 tracking-tight">
                {searchTerm || selectedUser !== 'all' || Object.values(filters).some(f => f !== 'all') ? 'No Matching Trips Found' : 'Your Portfolio is Empty'}
              </h3>
              <p className="text-white/60 mb-8 max-w-md mx-auto font-normal leading-relaxed">
                {searchTerm || selectedUser !== 'all' || Object.values(filters).some(f => f !== 'all')
                  ? 'Try adjusting your search or filter criteria to find what you\'re looking for.'
                  : 'Start your journey by searching for flights.'
                }
              </p>
              {!searchTerm && selectedUser === 'all' && Object.values(filters).every(f => f === 'all') && (
                <Link to={createPageUrl('search')}>
                  <Button className="bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:border-white/30 transition-all duration-300 px-6 h-11">
                    <Plane className="w-4 h-4 mr-2" />
                    Search Flights
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>
        )}

        <FlightDetailsModal
          trip={selectedTrip}
          isOpen={showFlightModal}
          onClose={closeFlightModal}
          onUpdate={handleModalUpdate}
          currentUser={user}
          userMap={userMap}
          deferaZeroPaymentSuccessBookingId={deferaZeroPaymentSuccessBookingId}
          onDeferaZeroPaymentSuccessHandled={handleDeferaZeroPaymentSuccessHandled}
        />
      </div>
    </div>
    </>
  );
}
